/**
 * AnnouncementCallsPanel
 *
 * Self-contained UI for triggering announcement calls to selected students'
 * parents via Retell. Embedded into VoiceCalls (admin) and StudentsPage (teacher).
 *
 * Props:
 *   classFilter        — admin: "all" | classId  | teacher: assigned class
 *   sectionFilter      — admin: "all" | section  | teacher: assigned section
 *   constrainToTeacher — boolean, hides the class/section pickers when true
 *   onSent             — callback after a successful queue
 */
import { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Megaphone, Send, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MAX_CHARS, StudentPicker, TemplatesHistoryPanel } from "@/components/announcement/AnnouncementParts";

export default function AnnouncementCallsPanel({
  isOpen,
  onClose,
  defaultClass,
  defaultSection,
  presetStudents,           // optional array — when caller already has a curated list
  isTeacher = false,
  onSent,
}) {
  const [meta, setMeta] = useState({ classes: [], sections: [] });
  const [cls, setCls] = useState(defaultClass || "");
  const [sec, setSec] = useState(defaultSection || "");
  const [search, setSearch] = useState("");
  const [students, setStudents] = useState([]);    // available for selection
  const [picked, setPicked] = useState(() => new Set((presetStudents || []).map((s) => s.id)));
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState({ items: [], counters: { total: 0, success: 0, failed: 0, skipped: 0, pending: 0 } });
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Load templates + initial history
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const [t, h] = await Promise.all([
          api.get("/call-templates"),
          api.get("/announcements/calls", { params: { limit: 50 } }),
        ]);
        setTemplates(t.data || []);
        setHistory(h.data || { items: [], counters: {} });
      } catch (e) {
        toast.error(apiErr(e.response?.data?.detail) || "Could not load announcement panel");
      }
    })();
    if (!isTeacher) {
      api.get("/meta/classes").then(({ data }) => setMeta({ classes: data.classes, sections: data.sections })).catch(() => {});
    }
  }, [isOpen, isTeacher]);

  // When admin picks class+section, fetch the roster
  useEffect(() => {
    if (!isOpen) return;
    if (presetStudents && presetStudents.length) {
      setStudents(presetStudents);
      return;
    }
    if (!cls || !sec) {
      setStudents([]);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/students", { params: { class_name: cls, section: sec } });
        setStudents(Array.isArray(data) ? data : (data.items || []));
      } catch (e) {
        toast.error(apiErr(e.response?.data?.detail) || "Could not load roster");
      }
    })();
  }, [isOpen, cls, sec, presetStudents]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => (s.name || "").toLowerCase().includes(q) || (s.roll_number || "").toLowerCase().includes(q));
  }, [students, search]);

  const toggleAll = () => {
    if (picked.size === filtered.length && filtered.length > 0) {
      setPicked(new Set());
    } else {
      setPicked(new Set(filtered.map((s) => s.id)));
    }
  };

  const togglePick = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const applyTemplate = (tid) => {
    setTemplateId(tid);
    const t = templates.find((x) => x.id === tid);
    if (t) setMessage(t.template || t.message || "");
  };

  const reloadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await api.get("/announcements/calls", { params: { limit: 50 } });
      setHistory(data || { items: [], counters: {} });
    } catch (e) { /* silent */ }
    finally { setLoadingHistory(false); }
  };

  const send = async () => {
    const ids = Array.from(picked);
    if (ids.length === 0) { toast.error("Pick at least one student"); return; }
    const msg = message.trim();
    if (msg.length < 5) { toast.error("Message must be at least 5 characters"); return; }
    if (msg.length > MAX_CHARS) { toast.error(`Message exceeds ${MAX_CHARS} characters`); return; }
    setSending(true);
    try {
      const { data } = await api.post("/announcements/calls", {
        student_ids: ids,
        message: msg,
        template_id: templateId || null,
      });
      toast.success(`Queued ${data.queued} call${data.queued === 1 ? "" : "s"} (1 per second)`);
      setPicked(new Set());
      setMessage("");
      setTemplateId("");
      if (onSent) onSent(data);
      // give a tiny moment for the first call to record
      setTimeout(reloadHistory, 1500);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not queue calls");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="announcement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Megaphone className="h-5 w-5 text-violet-500" />
            Send announcement call
          </DialogTitle>
          <DialogDescription>Pick students, write or choose a message, and place an automated voice call to their parents.</DialogDescription>
        </DialogHeader>

        <div className="grid lg:grid-cols-[1fr_320px] gap-5">
          <StudentPicker
            isTeacher={isTeacher}
            presetStudents={presetStudents}
            meta={meta}
            cls={cls}
            setCls={setCls}
            sec={sec}
            setSec={setSec}
            search={search}
            setSearch={setSearch}
            filtered={filtered}
            picked={picked}
            toggleAll={toggleAll}
            togglePick={togglePick}
            message={message}
            setMessage={setMessage}
          />

          <TemplatesHistoryPanel
            templates={templates}
            templateId={templateId}
            applyTemplate={applyTemplate}
            history={history}
            loadingHistory={loadingHistory}
            reloadHistory={reloadHistory}
          />
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="ann-cancel">
            <X className="h-4 w-4 mr-1" /> Close
          </Button>
          <Button onClick={send} disabled={sending || picked.size === 0 || message.trim().length < 5}
                  className="bg-violet-500 hover:bg-violet-600" data-testid="ann-send-btn">
            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            {sending ? "Queueing…" : `Call ${picked.size || ""} parent${picked.size === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
